import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Block, DefaultText, Spinner } from '@guryou/html-components';
import { Box, Button, TextField } from '@material-ui/core';
import { useAiContext } from 'aiStudio/context/AiContext';
import ButtonGroup from '../components/ButtonGroup';
import WeeksCounter from '../components/WeeksCounter';
import { ageOptions, difficultyOptions, genderOptions, goalOptions, muscleGroupOptions } from '../options';

const TrainingPlanView = ({ classes }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [generatedPrompt, setGeneratedPrompt] = useState('');
  const [weeks, setWeeks] = useState(1);

  const history = useHistory();

  const {
    selectedAge,
    setSelectedAge,
    selectedGender,
    setSelectedGender,
    selectedGoal,
    setSelectedGoal,
    selectedDifficulty,
    setSelectedDifficulty,
    selectedMuscleGroups,
    setSelectedMuscleGroups,
  } = useAiContext();

  const bottomRef = useRef(null);

  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);
  useEffect(() => {
    const muscleGroupsText = selectedMuscleGroups.length > 0 ? `focusing on ${selectedMuscleGroups.join(', ')} muscle groups` : '';

    const prompt = `Create a ${selectedDifficulty} ${weeks} week workout plan for a ${selectedAge} year old ${selectedGender} with a goal of ${selectedGoal} ${muscleGroupsText}. ${input}`.trim();

    setGeneratedPrompt(prompt);
  }, [selectedAge, selectedDifficulty, selectedGoal, selectedGender, selectedMuscleGroups, input, weeks]);

  const handleSend = async () => {
    if (generatedPrompt.trim() === '') {
      return;
    }

    setMessages(prev => [...prev, { text: generatedPrompt, sender: 'user' }]);
    setLoading(true);

    try {
      const response = await fetch('https://betqswdkzhrihd4nkp2rscg3ai0hjwhj.lambda-url.eu-west-1.on.aws', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          chatInput: generatedPrompt,
        }),
      });
      const responseText = await response.text();
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        throw new Error('Invalid JSON response from server');
      }
      setMessages(prev => [
        ...prev,
        {
          text: `Response received! Redirecting to preview...`,
          sender: 'ai',
        },
      ]);
      sessionStorage.setItem('aiWorkoutPlanData', responseText);
      setTimeout(() => {
        history.push('guryou-ai-studio/preview/training-plan');
      }, 1500);
    } catch (error) {
      setMessages(prev => [
        ...prev,
        {
          text: `Error: ${error.message}`,
          sender: 'system',
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const isButtonDisabled = loading || generatedPrompt.trim() === '';

  return (
    <>
      <Box
        style={{
          flexGrow: 1,
          overflowY: 'auto',
          backgroundColor: '#212121',
          padding: '16px',
          borderRadius: 8,
          margin: '8px 0',
          minHeight: 0,
        }}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', columnGap: '20px' }}>
          <ButtonGroup title="Gender" options={genderOptions} value={selectedGender} onChange={setSelectedGender} />
          <ButtonGroup title="Age range" options={ageOptions} value={selectedAge} onChange={setSelectedAge} />
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', columnGap: '20px' }}>
          <ButtonGroup title="Difficulty" options={difficultyOptions} value={selectedDifficulty} onChange={setSelectedDifficulty} />
          <WeeksCounter title="Weeks" value={weeks} onChange={setWeeks} />
        </div>
        <ButtonGroup title="Goal" options={goalOptions} value={selectedGoal} onChange={setSelectedGoal} />
        <ButtonGroup
          title="Muscle groups"
          isMultiSelect={true}
          options={muscleGroupOptions}
          value={selectedMuscleGroups}
          onChange={setSelectedMuscleGroups}
        />

        <Block width="100%" margin={0} marginTop={2} marginBottom={2} padding={2}>
          <DefaultText color="#aaa" fontSize="0.9rem">
            Current prompt:
          </DefaultText>
          <DefaultText color="#fff">{generatedPrompt}</DefaultText>
        </Block>

        {messages.map((msg, index) => (
          <Block key={index} width="auto" display="flex" justifyContent={msg.sender === 'user' ? 'flex-end' : 'flex-start'} marginBottom={2}>
            <DefaultText
              display="inline-block"
              width="auto"
              color="#ccc"
              padding={2.6}
              backgroundColor={msg.sender === 'user' ? '#303030' : msg.sender === 'ai' ? '#1a5d2c' : '#8b0000'}
              borderRadius="25px"
              whiteSpace="pre-wrap"
              maxWidth="80%"
            >
              {msg?.text}
            </DefaultText>
          </Block>
        ))}
        <div ref={bottomRef} />
      </Box>

      <Block display="flex" overflowX="hidden" margin={0} backgroundColor="#303030" borderRadius="10px" border="1px solid #434343" flexShrink={0}>
        <TextField
          variant="outlined"
          multiline
          className={classes.chatInput}
          fullWidth
          placeholder="Add additional details to your workout request (optional)"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          disabled={loading}
        />
        {loading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 20px' }}>
            <Spinner size={30} />
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 20px' }}>
            <Button variant="contained" color="primary" onClick={handleSend} disabled={isButtonDisabled}>
              Send
            </Button>
          </div>
        )}
      </Block>
    </>
  );
};

export default TrainingPlanView;
